import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { DetailSection } from "@/components/freelancers/DetailSection";
import { ClipboardCheck, Calendar, User, Star, ChevronDown, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { useUserPermissions } from "@/hooks/use-user-permissions";
import type { EvaluationFormItem, EvaluationResponse } from "@/types/evaluation-form";
import { reportUserError } from "@/lib/report-error";

interface EvaluationWithDetails {
  id: number;
  group_id: number;
  group_name: string;
  evaluated_at: string | null;
  evaluated_by: string | null;
  evaluator_name: string | null;
  evaluator_weight: number | null;
  nota_criterio_pessoal: number | null;
  compativel_grana: string | null;
  outras_percepcoes: string | null;
  form_id: number | null;
}

interface FreelancerAvaliacoesProps {
  freelancerId: string;
}

export function FreelancerAvaliacoes({ freelancerId }: FreelancerAvaliacoesProps) {
  const { screens } = useUserPermissions();
  // O gate real está na RPC (SECURITY DEFINER); aqui é só para não mostrar uma
  // ação que o banco vai recusar.
  const canDelete = screens.includes("gestao_perfis_usuarios");

  // Fetch evaluations for this freelancer
  const { data: avaliacoes, isLoading } = useQuery({
    queryKey: ["freelancer-avaliacoes", freelancerId],
    queryFn: async () => {
      // Get evaluations from v_evaluation_group_freelancers_detail
      const { data: evaluations, error } = await supabase
        .from("v_evaluation_group_freelancers_detail")
        .select("*")
        .eq("freelancer_id", freelancerId)
        .eq("is_evaluated", true)
        .order("evaluated_at", { ascending: false });

      if (error) throw error;
      if (!evaluations || evaluations.length === 0) return [];

      // Get evaluator names
      const evaluatorIds = [...new Set(evaluations.map(e => e.evaluated_by).filter(Boolean))];
      const { data: evaluators } = await supabase
        .from("profiles")
        .select("id, full_name")
        .in("id", evaluatorIds);

      // Get form IDs from groups
      const groupIds = [...new Set(evaluations.map(e => e.group_id))];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: groups } = await (supabase as any)
        .from("evaluation_groups")
        .select("id, form_id")
        .in("id", groupIds);

      // Map evaluations with evaluator names and form IDs
      return evaluations.map(evaluation => {
        const evaluator = evaluators?.find(e => e.id === evaluation.evaluated_by);
        const group = groups?.find((g: { id: number }) => g.id === evaluation.group_id);
        return {
          id: evaluation.id,
          group_id: evaluation.group_id,
          group_name: evaluation.group_name,
          evaluated_at: evaluation.evaluated_at,
          evaluated_by: evaluation.evaluated_by,
          evaluator_name: evaluator?.full_name || null,
          evaluator_weight: evaluation.evaluator_weight,
          nota_criterio_pessoal: evaluation.nota_criterio_pessoal,
          compativel_grana: evaluation.compativel_grana,
          outras_percepcoes: evaluation.outras_percepcoes,
          form_id: group?.form_id || null,
        } as EvaluationWithDetails;
      });
    },
    enabled: !!freelancerId,
  });

  if (isLoading) {
    return (
      <DetailSection title="Avaliações" icon={ClipboardCheck}>
        <div className="space-y-3">
          <Skeleton className="h-20 w-full rounded-lg" />
          <Skeleton className="h-20 w-full rounded-lg" />
        </div>
      </DetailSection>
    );
  }

  if (!avaliacoes || avaliacoes.length === 0) {
    return (
      <DetailSection title="Avaliações" icon={ClipboardCheck}>
        <p className="text-sm text-muted-foreground">
          Este freelancer ainda não possui avaliações.
        </p>
      </DetailSection>
    );
  }

  return (
    <DetailSection title="Avaliações" icon={ClipboardCheck} count={avaliacoes.length}>
      <div className="space-y-2">
        {avaliacoes.map((avaliacao) => (
          <AvaliacaoCard
            key={avaliacao.id}
            avaliacao={avaliacao}
            freelancerId={freelancerId}
            canDelete={canDelete}
          />
        ))}
      </div>
    </DetailSection>
  );
}

interface AvaliacaoCardProps {
  avaliacao: EvaluationWithDetails;
  freelancerId: string;
  canDelete: boolean;
}

function AvaliacaoCard({ avaliacao, freelancerId, canDelete }: AvaliacaoCardProps) {
  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const refreshAfterChange = () => {
    queryClient.invalidateQueries({ queryKey: ["freelancer-avaliacoes", freelancerId] });
    queryClient.invalidateQueries({ queryKey: ["freelancer", freelancerId] });
  };

  const handleRestore = async () => {
    const { data, error } = await supabase.rpc("restore_avaliacao_perfil", {
      p_evaluation_id: avaliacao.id,
    });
    const result = data as { ok?: boolean; motivo?: string } | null;

    if (error || !result?.ok) {
      toast({
        variant: "destructive",
        title: "Não foi possível restaurar",
        description: error
          ? reportUserError(error, { source: "freelancer-avaliacoes" }).description
          : MOTIVO_LABEL[result?.motivo ?? ""] ?? "Tente novamente.",
      });
      return;
    }

    refreshAfterChange();
    toast({ title: "Avaliação restaurada", description: "Ela volta a contar na média." });
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const { data, error } = await supabase.rpc("soft_delete_avaliacao_perfil", {
        p_evaluation_id: avaliacao.id,
        p_reason: deleteReason.trim() || null,
      });
      if (error) throw error;

      const result = data as { ok?: boolean; motivo?: string } | null;
      if (!result?.ok) {
        toast({
          variant: "destructive",
          title: "Nada foi excluído",
          description: MOTIVO_LABEL[result?.motivo ?? ""] ?? "Recarregue a página e tente de novo.",
        });
        return;
      }

      refreshAfterChange();
      setConfirmDelete(false);
      setDeleteReason("");
      toast({
        title: "Avaliação excluída",
        description: "Ela saiu da média. O registro fica guardado e pode ser restaurado.",
        action: (
          <ToastAction altText="Desfazer exclusão" onClick={handleRestore}>
            Desfazer
          </ToastAction>
        ),
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Erro ao excluir avaliação",
        description: (err as Error).message,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Detalhes só carregam quando o card é expandido.
  const { data: formItems = [] } = useQuery({
    queryKey: ["evaluation_form_items", avaliacao.form_id],
    queryFn: async () => {
      if (!avaliacao.form_id) return [];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("evaluation_form_items")
        .select("*")
        .eq("form_id", avaliacao.form_id)
        .order("display_order");

      if (error) throw error;
      return data as EvaluationFormItem[];
    },
    enabled: !!avaliacao.form_id && open,
  });

  // Fetch evaluation responses
  const { data: responses = [] } = useQuery({
    queryKey: ["evaluation_responses", avaliacao.id],
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("evaluation_responses")
        .select("*")
        .eq("evaluation_group_freelancer_id", avaliacao.id);

      if (error) throw error;
      return data as EvaluationResponse[];
    },
    enabled: open,
  });

  const getResponseValue = (itemId: number): number | null => {
    const response = responses.find(r => r.form_item_id === itemId);
    return response?.value ?? null;
  };

  const getScoreColor = (score: number | null) => {
    if (!score) return "bg-gray-200";
    if (score >= 8) return "bg-green-500";
    if (score >= 6) return "bg-yellow-500";
    return "bg-red-500";
  };

  const getScoreIndicatorClass = (score: number | null) => {
    const colorClass = getScoreColor(score);
    const map: Record<string, string> = {
      "bg-green-500": "[&>div]:bg-green-500",
      "bg-yellow-500": "[&>div]:bg-yellow-500",
      "bg-red-500": "[&>div]:bg-red-500",
      "bg-gray-200": "[&>div]:bg-gray-200",
    };
    return map[colorClass] || "[&>div]:bg-primary";
  };

  const getBudgetLabel = (value: string | null) => {
    if (!value) return null;
    const labels: Record<string, string> = {
      menos: "Abaixo da média",
      neutro: "Na média",
      mais: "Acima da média",
    };
    return labels[value] || value;
  };

  const hasDetails =
    !!avaliacao.form_id || !!avaliacao.compativel_grana || !!avaliacao.outras_percepcoes;

  return (
    <Card className="overflow-hidden border-l-4 border-l-primary">
      <Collapsible open={open} onOpenChange={setOpen}>
        {/* Cabeçalho compacto — clique expande os detalhes */}
        <CollapsibleTrigger className="flex w-full items-center gap-3 p-3 text-left hover:bg-muted/30">
          <div className="min-w-0 flex-1 space-y-0.5">
            <h3 className="truncate text-sm font-semibold">{avaliacao.group_name}</h3>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
              {avaliacao.evaluated_at && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {format(new Date(avaliacao.evaluated_at), "dd/MM/yyyy", { locale: ptBR })}
                </span>
              )}
              {avaliacao.evaluator_name && (
                <span className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {avaliacao.evaluator_name}
                </span>
              )}
              {avaliacao.evaluator_weight && (
                <span>Peso: {avaliacao.evaluator_weight}</span>
              )}
            </div>
          </div>
          {avaliacao.nota_criterio_pessoal !== null && (
            <span className="flex shrink-0 items-center gap-1 rounded-md bg-primary/10 px-2 py-1 text-sm font-bold text-primary">
              <Star className="h-3.5 w-3.5" />
              {avaliacao.nota_criterio_pessoal}
              <span className="text-[10px] font-normal text-muted-foreground">/10</span>
            </span>
          )}
          {hasDetails && (
            <ChevronDown
              className={cn(
                "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                open && "rotate-180",
              )}
            />
          )}
          {canDelete && (
            // Dentro do CollapsibleTrigger, então precisa barrar a propagação
            // para o clique não expandir o card junto.
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
              aria-label="Excluir avaliação"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setConfirmDelete(true);
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="space-y-4 border-t bg-muted/10 p-4">
            {/* Form items with responses */}
            {formItems.length > 0 && (
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                {formItems.map((item) => {
                  const value = getResponseValue(item.id);
                  return (
                    <div key={item.id} className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="font-medium text-muted-foreground truncate pr-2">
                          {item.label}
                        </span>
                        <span className="font-bold">{value ?? "-"}</span>
                      </div>
                      <Progress
                        value={value ? (value / item.max_value) * 100 : 0}
                        className={cn("h-1", getScoreIndicatorClass(value))}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {/* Budget compatibility */}
            {avaliacao.compativel_grana && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Orçamento:</span>
                <Badge
                  variant={avaliacao.compativel_grana === 'neutro' ? 'secondary' : avaliacao.compativel_grana === 'mais' ? 'destructive' : 'default'}
                  className="text-xs"
                >
                  {getBudgetLabel(avaliacao.compativel_grana)}
                </Badge>
              </div>
            )}

            {/* Additional notes */}
            {avaliacao.outras_percepcoes && (
              <div className="bg-muted/30 p-2 rounded text-xs text-muted-foreground italic border-l-2 border-muted">
                "{avaliacao.outras_percepcoes}"
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir esta avaliação?</AlertDialogTitle>
            <AlertDialogDescription>
              A avaliação de "{avaliacao.group_name}"
              {avaliacao.nota_criterio_pessoal !== null
                ? ` (nota ${avaliacao.nota_criterio_pessoal})`
                : ""}{" "}
              sai da média do freelancer e deixa de aparecer no perfil. O registro
              continua guardado no banco e dá para restaurar depois.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <label htmlFor={`motivo-${avaliacao.id}`} className="text-sm font-medium">
              Motivo (opcional)
            </label>
            <Textarea
              id={`motivo-${avaliacao.id}`}
              value={deleteReason}
              onChange={(event) => setDeleteReason(event.target.value)}
              placeholder="Ex.: avaliação de teste"
              rows={2}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={isDeleting}
              onClick={(event) => {
                event.preventDefault();
                handleDelete();
              }}
            >
              {isDeleting ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

const MOTIVO_LABEL: Record<string, string> = {
  nao_encontrada_ou_ja_excluida: "Esta avaliação já havia sido excluída.",
  nao_encontrada_nao_excluida_ou_slot_ocupado:
    "Não foi possível restaurar: o freelancer já tem uma avaliação ativa neste grupo.",
};

